import { QueryClient } from '@tanstack/react-query'

const RETRYABLE_ATTEMPTS = 2

/**
 * Client errors are never retried — only transport failures and 5xx are worth a
 * second attempt.
 */
const shouldRetry = (failureCount, error) => {
  if (failureCount >= RETRYABLE_ATTEMPTS) return false
  const status = error?.status
  if (error?.isNetworkError) return true
  return typeof status === 'number' && status >= 500
}

/**
 * Read data is always revalidated.
 *
 * `staleTime: 0` does not mean "no cache" — `gcTime` still keeps responses, so a
 * revisited page paints instantly from the cached copy and refetches behind it.
 * What it removes is the window where a stale copy was served with no request at
 * all, which on an operational dashboard showed a queue that had already moved
 * on. Individual queries may still opt out where the data genuinely cannot
 * change: `useRolesQuery` is the only one that does.
 *
 * The cache is keyed by endpoint and parameters, never by who asked, so it is
 * cleared outright on sign-in and sign-out — see `authStore.logoutService`.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 0,
      gcTime: 5 * 60_000,
      retry: shouldRetry,
      refetchOnWindowFocus: true,
      throwOnError: false,
    },
    mutations: {
      retry: false,
    },
  },
})
